import { useState } from "react";
import type { FactoryProfile } from "@/mocks/factoryProfiles";

interface Props {
  factory: FactoryProfile;
  onContact: (factory: FactoryProfile) => void;
  onRFQ: (factory: FactoryProfile) => void;
}

export default function FactoryProfileCard({ factory, onContact, onRFQ }: Props) {
  const [expanded, setExpanded] = useState(false);
  const yearsInBusiness = 2026 - factory.founded;

  return (
    <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden hover:border-amber-200 transition-all duration-300">
      {/* Cover */}
      <div className="relative w-full h-36 overflow-hidden">
        <img src={factory.coverImage} alt={factory.name} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-1.5">
          {factory.goldSupplier && (
            <span className="flex items-center gap-1 bg-amber-400 text-white text-xs font-bold px-2.5 py-1 rounded-full">
              <i className="ri-medal-line"></i> Gold Supplier
            </span>
          )}
          {factory.verified && (
            <span className="flex items-center gap-1 bg-green-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
              <i className="ri-shield-check-line"></i> Verified
            </span>
          )}
        </div>
        {/* Response time */}
        <div className="absolute top-3 right-3 bg-white/90 text-gray-700 text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
          <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
          Replies {factory.responseTime}
        </div>
      </div>

      {/* Factory avatar + name */}
      <div className="px-5 pt-4 pb-3">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0 border-2 border-amber-100 -mt-8 relative z-10 bg-white">
            <img src={factory.image} alt={factory.name} className="w-full h-full object-cover object-top" />
          </div>
          <div className="flex-1 min-w-0 pt-1">
            <h3 className="font-extrabold text-gray-900 text-sm leading-tight line-clamp-2">{factory.name}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{factory.flag} {factory.city}, {factory.country}</p>
          </div>
        </div>

        {/* Key stats row */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          {[
            { icon: "ri-calendar-line", label: "Est.", value: factory.founded.toString() },
            { icon: "ri-team-line", label: "Staff", value: factory.employees },
            { icon: "ri-global-line", label: "Export", value: `${factory.exportCountries}+ Countries` },
          ].map(s => (
            <div key={s.label} className="bg-gray-50 rounded-xl p-2 text-center">
              <i className={`${s.icon} text-amber-400 text-sm block mb-0.5`}></i>
              <p className="text-xs font-extrabold text-gray-800 leading-tight">{s.value}</p>
              <p className="text-xs text-gray-400">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Production capacity */}
        <div className="flex items-center gap-2 bg-amber-50 rounded-xl px-3 py-2 mb-3">
          <i className="ri-building-2-line text-amber-500 shrink-0"></i>
          <div className="min-w-0">
            <p className="text-xs font-bold text-amber-700 truncate">{factory.productionCapacity}</p>
            <p className="text-xs text-gray-400">{yearsInBusiness} years in business · {factory.annualRevenue} revenue</p>
          </div>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center gap-0.5">
            {[1,2,3,4,5].map(s => (
              <i key={s} className={`${s <= Math.round(factory.rating) ? "ri-star-fill" : "ri-star-line"} text-amber-400 text-xs`}></i>
            ))}
          </div>
          <span className="text-xs font-bold text-gray-700">{factory.rating}</span>
          <span className="text-xs text-gray-400">({factory.reviews.toLocaleString()} reviews)</span>
        </div>

        {/* Certifications */}
        <div className="mb-3">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Certifications</p>
          <div className="flex flex-wrap gap-1">
            {factory.certifications.slice(0, expanded ? undefined : 5).map(cert => (
              <span key={cert} className="text-xs bg-sky-50 text-sky-700 border border-sky-200 font-semibold px-2 py-0.5 rounded-full">
                {cert}
              </span>
            ))}
            {!expanded && factory.certifications.length > 5 && (
              <button
                onClick={() => setExpanded(true)}
                className="text-xs text-amber-500 font-semibold cursor-pointer hover:underline"
              >
                +{factory.certifications.length - 5} more
              </button>
            )}
          </div>
        </div>

        {/* Specialties */}
        <div className="mb-4">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Specialties</p>
          <div className="flex flex-wrap gap-1">
            {factory.specialties.map(s => (
              <span key={s} className="text-xs bg-gray-100 text-gray-600 font-semibold px-2 py-0.5 rounded-full">{s}</span>
            ))}
          </div>
        </div>

        {/* Top products */}
        <div className="mb-4">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Top Products</p>
          <div className="flex flex-col gap-1.5">
            {factory.products.map(p => (
              <div key={p.name} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                <span className="text-xs text-gray-700 font-medium line-clamp-1 flex-1 mr-2">{p.name}</span>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs font-bold text-amber-500">{p.price}</span>
                  <span className="text-xs text-gray-400">MOQ: {p.minOrder}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Description */}
        <p className="text-xs text-gray-500 leading-relaxed mb-4 line-clamp-3">{factory.description}</p>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={() => onRFQ(factory)}
            className="flex-1 bg-amber-400 hover:bg-amber-500 text-white text-xs font-bold py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-list-3-line mr-1"></i>Request Quote
          </button>
          <button
            onClick={() => onContact(factory)}
            className="flex-1 border border-gray-200 hover:border-amber-300 text-gray-600 hover:text-amber-600 text-xs font-bold py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-message-3-line mr-1"></i>Chat Now
          </button>
        </div>
      </div>
    </div>
  );
}
